import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Box,
  Link,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SecurityIcon from '@mui/icons-material/Security';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CategoryChip = styled(Chip)(({ theme, category }) => {
  const colorMap = {
    'Infosec': '#d32f2f',
    'AI': '#1976d2',
    'DevOps': '#388e3c',
    'Data': '#f57c00',
  };
  
  return {
    backgroundColor: colorMap[category] || '#757575',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

const ExpertSummaryBox = styled(Paper)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

const NewsCard = ({ article }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <StyledCard>
      {article.thumbnail_url && (
        <CardMedia
          component="img"
          height="180"
          image={article.thumbnail_url}
          alt={article.title}
          sx={{ objectFit: 'cover' }}
        />
      )}
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <CategoryChip 
            label={article.category} 
            category={article.category}
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            {formatDate(article.date_published || article.date_scraped)}
          </Typography>
        </Box>
        
        <Link
          href={article.original_url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            color: 'text.primary',
            '&:hover': { color: 'primary.main' },
            mb: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
          }}
        >
          <Typography variant="h6" component="h3" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {article.title}
          </Typography>
          <OpenInNewIcon fontSize="small" sx={{ mt: 0.5, flexShrink: 0 }} />
        </Link>
        
        {article.summary && (
          <ExpertSummaryBox elevation={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SecurityIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                Cyber Expert Analysis
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {article.summary}
            </Typography>
          </ExpertSummaryBox>
        )}
        
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ mt: 'auto', pt: 2, fontStyle: 'italic' }}
        >
          Source: {article.source}
        </Typography>
      </CardContent>
    </StyledCard>
  );
};

export default NewsCard;
